import type { IReactionDisposer } from 'mobx';
import { action, computed, makeObservable, observable, reaction } from 'mobx';

import { postRequest } from '../../services/api';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import { devError } from '../../utils/log';
import type { EventTriggerPopupData } from '../event-trigger/types';
import { EVENT_TRIGGER_ACTION_TYPE } from '../event-trigger/types';
import type { FeatureFetchStatus, FeatureManagerDialogType, IFeatureManagerStore } from '../feature-manager/types';
import type { RootStore } from '../RootStore';

import { FINISH_COIN_TUTORIAL_TIMEOUT_MS, SUPPORTED_ACTION_TYPES } from './constants';
import type { RiveCoinsAnimationRef } from './types';

export class ActionEventTriggerStore implements IFeatureManagerStore {
  private readonly rootStore: RootStore;
  private _coinAnimationRef: RiveCoinsAnimationRef | null = null;
  private _registry: Map<string, EventTriggerPopupData> = new Map([]);
  private _coinTutorialTimeout: NodeJS.Timeout | null = null;
  private _dialogListenerDisposer: IReactionDisposer | null = null;
  private _slideListenerDisposer: IReactionDisposer | null = null;
  private _coinTutorialBlocked = false;

  private _disappearTimeout: NodeJS.Timeout | null = null;

  constructor(rootStore: RootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
    ldClient?.on('change', (changes) => {
      if ('event_trigger_actions' in changes) {
        this.onFeatureFlagChange(changes.event_trigger_actions.current);
      }
    });
    reaction(
      () => this.rootStore.coinTypeStore.coinType,
      (coinType) => {
        if (coinType === 'GC') {
          this._coinTutorialBlocked = false;
          this.rootStore.featureManagerStore.addDialogsToSequenceQueue(['EventTriggerActionCoinTutorial']);
        }
      },
    );
  }

  @observable
  public playingCoinTutorial: boolean = false;

  @action
  public setPlayingCoinTutorial(value: boolean) {
    this.playingCoinTutorial = value;
  }

  @computed
  public get currentCoinTutorialEvent() {
    const eventId = this.coinTutorialsQueue.size ? this.coinTutorialsQueue.values().next().value : null;
    return eventId ? this._registry.get(eventId) : null;
  }

  public dialogTypes: FeatureManagerDialogType[] = ['EventTriggerActionCoinTutorial'];

  public coinTutorialsQueue = new Set<string>([]);

  public async fetch(): Promise<FeatureFetchStatus> {
    return new Promise((resolve) => resolve('READY'));
  }

  public playCoinTutorial() {
    this._slideListenerDisposer = reaction(
      () => this.rootStore.uiStore.prevSlide,
      (slide) => {
        if (slide) {
          this.interruptCoinTutorial();
        }
      },
    );
    this._dialogListenerDisposer = reaction(
      () => this.rootStore.uiStore.isDialogOpen,
      (dialogOpen) => {
        if (dialogOpen) {
          this.interruptCoinTutorial();
        }
      },
    );
    if (this._coinTutorialTimeout) {
      clearTimeout(this._coinTutorialTimeout);
    }
    this._coinTutorialTimeout = setTimeout(this.finishCoinTutorial, FINISH_COIN_TUTORIAL_TIMEOUT_MS);
    this.setPlayingCoinTutorial(true);
  }

  public setCoinAnimationRef(ref: RiveCoinsAnimationRef | null) {
    this._coinAnimationRef = ref;
  }

  public finishCoinTutorial = () => {
    const eventId = this.coinTutorialsQueue.size ? this.coinTutorialsQueue.values().next().value : '';
    const event = this._registry.get(eventId);
    if (event) {
      this.removeFromCoinTutorialsQueue(eventId);
      void this.acknowledgeAction(event);
      this.cleanDisposers();
      this.rootStore.featureManagerStore.onDialogFinished('EventTriggerActionCoinTutorial');
      if (this.coinTutorialsQueue.size) {
        this.rootStore.featureManagerStore.addDialogsToSequenceQueue(['EventTriggerActionCoinTutorial']);
      }
    }
    this.interruptCoinTutorial();
  };

  public registerEvents = (events: EventTriggerPopupData[]) => {
    const featureFlag = resolveFlag('event_trigger_actions', false);
    if (!featureFlag) {
      return;
    }
    if (!events.length) {
      return;
    }
    events.forEach(this.registerActionEvent);
    if (this.coinTutorialsQueue.size) {
      this.rootStore.featureManagerStore.addDialogsToSequenceQueue(['EventTriggerActionCoinTutorial']);
    }
  };

  public startDialogs(type?: FeatureManagerDialogType | undefined): boolean {
    switch (type) {
      case 'EventTriggerActionCoinTutorial': {
        if (!this.coinTutorialsQueue.size || this._coinTutorialBlocked) {
          return false;
        }
        const isProperCoin = this.rootStore.coinTypeStore.coinType === 'GC';
        const scModeAvailable = resolveFlag('sc_mode_available', false);
        if (isProperCoin && scModeAvailable) {
          this.playCoinTutorial();
          return true;
        }
        this._coinTutorialBlocked = true;
        return false;
      }
    }
    return false;
  }

  private clear() {
    this._registry.clear();
    this.clearCoinTutorialsQueue();
    if (this._coinTutorialTimeout) {
      clearTimeout(this._coinTutorialTimeout);
    }
    this._coinTutorialTimeout = null;
  }

  private addToCoinTutorialsQueue(value: string) {
    this.coinTutorialsQueue.add(value);
  }

  private removeFromCoinTutorialsQueue(value: string) {
    this.coinTutorialsQueue.delete(value);
  }

  private clearCoinTutorialsQueue() {
    this.coinTutorialsQueue = new Set([]);
  }

  private registerActionEvent = (event: EventTriggerPopupData) => {
    if (event.benefit.benefitType !== 'action') {
      return;
    }
    const actionType = event?.eventTrigger?.extra?.actionType;
    if (!actionType) {
      return;
    }
    if (!SUPPORTED_ACTION_TYPES.has(actionType)) {
      return;
    }
    switch (actionType) {
      case EVENT_TRIGGER_ACTION_TYPE.COIN_TUTORIAL:
        this.registerCoinTutorialEvent(event);
        break;
    }
  };

  private registerCoinTutorialEvent(event: EventTriggerPopupData) {
    this._registry.set(event.userEventTriggerId, event);
    this.addToCoinTutorialsQueue(event.userEventTriggerId);
  }

  private async acknowledgeAction(activeEvent: EventTriggerPopupData) {
    try {
      await postRequest('action-events/acknowledge', { activeEvent });
    } catch (error) {
      devError('AcknowledgeAction.post', error);
    }
  }

  private onFeatureFlagChange(ff: boolean | undefined) {
    if (!ff) {
      this.clear();
    }
  }

  private interruptCoinTutorial = () => {
    if (!this.playingCoinTutorial) {
      return;
    }
    this._coinAnimationRef?.stop();
    if (this._disappearTimeout) {
      clearTimeout(this._disappearTimeout);
    }
    // wait 300 ms to let play close animation
    this._disappearTimeout = setTimeout(() => {
      this.setPlayingCoinTutorial(false);
    }, 300);
  };

  private cleanDisposers() {
    if (this._dialogListenerDisposer) {
      this._dialogListenerDisposer();
      this._dialogListenerDisposer = null;
    }
    if (this._slideListenerDisposer) {
      this._slideListenerDisposer();
      this._slideListenerDisposer = null;
    }
  }
}
